import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dailyContentService, journalService, faithService, weeklyReflectionService, phaseTransitionService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import PhaseTransitionModal from '../components/PhaseTransitionModal';
import OnThisDay from '../components/OnThisDay';
import SOSButton from '../components/SOSButton';
import { Button, Card, Input, Textarea, Badge } from '../components/ui';

const DailyContent = () => {
  const { day } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [adaptive, setAdaptive] = useState(null);
  const [journalData, setJournalData] = useState({
    moodScale: 5,
    cravingScale: 5,
    sleepHours: 7,
    tags: '',
    freeWrite: '',
    responses: {},
    completed: {
      mind: false,
      move: false,
      journal: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [journalLocked, setJournalLocked] = useState(false);
  const [savedJournalContent, setSavedJournalContent] = useState('');
  const [journalAddition, setJournalAddition] = useState('');
  const [faithContent, setFaithContent] = useState(null);
  const [faithLoading, setFaithLoading] = useState(false);
  const [faithError, setFaithError] = useState(null);
  const [weeklyReflection, setWeeklyReflection] = useState({
    wentWell: '',
    challenges: '',
    avoided: '',
    selfRating: 5,
    focusNextWeek: ''
  });
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklySaving, setWeeklySaving] = useState(false);
  const [weeklyMessage, setWeeklyMessage] = useState(null);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionSaving, setTransitionSaving] = useState(false);

  const loadDailyContent = useCallback(async () => {
    if (!user?.currentDay) {
      setLoading(false);
      return;
    }

    try {
      const response = await dailyContentService.getAdaptiveDay(day || user.currentDay);
      setContent(response.data.data.content);
      setAdaptive(response.data.data.adaptive || null);
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        try {
          const fallback = await dailyContentService.getDay(day || user.currentDay);
          setContent(fallback.data.data.content);
          setAdaptive(null);
        } catch (fallbackError) {
          console.error('Failed to load daily content fallback:', fallbackError);
          setMessage({ type: 'error', text: 'No daily content found yet.' });
        }
      } else {
        console.error('Failed to load daily content:', error);
        setMessage({ type: 'error', text: 'Failed to load content' });
      }
    } finally {
      setLoading(false);
    }
  }, [day, user]);

  const loadJournalEntry = useCallback(async () => {
    if (!user?.id) return;

    const dayNumber = parseInt(day || user.currentDay, 10);
    if (!dayNumber) return;

    try {
      const response = await journalService.getEntry(user.id, dayNumber);
      const entry = response.data.data.entry;
      const contentData = entry?.content || {};
      const { freeWrite, ...promptResponses } = contentData;

      setJournalData(prev => ({
        ...prev,
        moodScale: entry?.tracking?.moodScale ?? prev.moodScale,
        cravingScale: entry?.tracking?.cravingScale ?? prev.cravingScale,
        sleepHours: entry?.tracking?.sleepHours ?? prev.sleepHours,
        tags: (entry?.tags || []).join(', '),
        freeWrite: freeWrite || '',
        responses: promptResponses || {},
        completed: {
          ...prev.completed,
          ...(entry?.completed || {})
        }
      }));

      const locked = Boolean(entry?.completed?.journal);
      setJournalLocked(locked);
      if (locked) {
        setSavedJournalContent(freeWrite || '');
      }
      setJournalAddition('');
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to load journal entry:', error);
      }
    }
  }, [day, user?.currentDay, user?.id]);

  useEffect(() => {
    loadDailyContent();
  }, [loadDailyContent]);

  useEffect(() => {
    loadJournalEntry();
  }, [loadJournalEntry]);

  useEffect(() => {
    const checkPhaseTransition = async () => {
      if (!user?.id) return;
      
      try {
        const response = await phaseTransitionService.getPending(user.id);
        const { hasPending, existing } = response.data.data;
        
        if (hasPending && !existing) {
          setPendingTransition(response.data.data);
          setShowTransitionModal(true);
        }
      } catch (error) {
        console.error('Failed to check phase transition:', error);
      }
    };
    
    checkPhaseTransition();
  }, [user?.id, day]);

  useEffect(() => {
    const loadWeeklyReflection = async () => {
      const dayNumber = parseInt(day || user?.currentDay, 10);

      if (!dayNumber || dayNumber % 7 !== 0) {
        setWeeklyReflection({
          wentWell: '',
          challenges: '',
          avoided: '',
          selfRating: 5,
          focusNextWeek: ''
        });
        setWeeklyMessage(null);
        setWeeklyLoading(false);
        return;
      }

      setWeeklyLoading(true);
      setWeeklyMessage(null);

      try {
        const weekNumber = Math.ceil(dayNumber / 7);
        const response = await weeklyReflectionService.getReflection(user.id, weekNumber);
        const reflection = response.data.data.reflection;
        setWeeklyReflection({
          wentWell: reflection.wentWell,
          challenges: reflection.challenges,
          avoided: reflection.avoided,
          selfRating: reflection.selfRating,
          focusNextWeek: reflection.focusNextWeek
        });
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Failed to load weekly reflection:', error);
          setWeeklyMessage({ type: 'error', text: 'Failed to load weekly reflection.' });
        }
      } finally {
        setWeeklyLoading(false);
      }
    };

    if (user?.id) {
      loadWeeklyReflection();
    }
  }, [day, user?.currentDay, user?.id]);

  useEffect(() => {
    const loadFaithContent = async () => {
      const dayNumber = parseInt(day || user?.currentDay, 10);

      if (!dayNumber || !user?.faithPreferences?.faithTrackEnabled) {
        setFaithContent(null);
        setFaithError(null);
        setFaithLoading(false);
        return;
      }

      setFaithLoading(true);
      setFaithError(null);

      try {
        const response = await faithService.getContentForDay(dayNumber);
        setFaithContent(response.data.data);
      } catch (error) {
        console.error('Failed to load faith content:', error);
        setFaithError('Faith content is not available yet.');
      } finally {
        setFaithLoading(false);
      }
    };

    loadFaithContent();
  }, [day, user?.currentDay, user?.faithPreferences?.faithTrackEnabled]);

  const handleJournalChange = (field, value) => {
    setJournalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResponseChange = (promptId, value) => {
    setJournalData(prev => ({
      ...prev,
      responses: {
        ...prev.responses,
        [promptId]: value
      }
    }));
  };

  const handleSubmit = async (pillar) => {
    setSaving(true);
    setMessage(null);

    try {
      const tagList = journalData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean);

      // For journal pillar: if already saved, append addition text
      let freeWriteContent = journalData.freeWrite;
      if (pillar === 'journal' && journalLocked && journalAddition.trim()) {
        freeWriteContent = savedJournalContent + '\n\n---\n\n' + journalAddition.trim();
      }

      const entryData = {
        day: parseInt(day || user.currentDay),
        content: {
          ...journalData.responses,
          freeWrite: freeWriteContent
        },
        tags: tagList,
        tracking: {
          moodScale: parseInt(journalData.moodScale),
          cravingScale: parseInt(journalData.cravingScale),
          sleepHours: parseFloat(journalData.sleepHours)
        },
        completed: {
          ...journalData.completed,
          [pillar]: true
        }
      };

      await journalService.createEntry(user.id, entryData);

      setJournalData(prev => ({
        ...prev,
        completed: {
          ...prev.completed,
          [pillar]: true
        },
        freeWrite: freeWriteContent
      }));

      if (pillar === 'journal') {
        setJournalLocked(true);
        setSavedJournalContent(freeWriteContent);
        setJournalAddition('');
      }

      setMessage({ type: 'success', text: `${pillar} activity completed!` });
    } catch (error) {
      console.error('Failed to save entry:', error);
      setMessage({ type: 'error', text: 'Failed to save entry' });
    } finally {
      setSaving(false);
    }
  };

  const handleWeeklyChange = (field, value) => {
    setWeeklyReflection(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWeeklySubmit = async () => {
    const dayNumber = parseInt(day || user.currentDay, 10);
    const weekNumber = Math.ceil(dayNumber / 7);

    setWeeklySaving(true);
    setWeeklyMessage(null);

    try {
      const payload = {
        day: dayNumber,
        weekNumber,
        wentWell: weeklyReflection.wentWell,
        challenges: weeklyReflection.challenges,
        avoided: weeklyReflection.avoided,
        selfRating: parseInt(weeklyReflection.selfRating, 10),
        focusNextWeek: weeklyReflection.focusNextWeek
      };

      await weeklyReflectionService.saveReflection(user.id, payload);
      setWeeklyMessage({ type: 'success', text: 'Weekly reflection saved.' });
    } catch (error) {
      console.error('Failed to save weekly reflection:', error);
      setWeeklyMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save weekly reflection.'
      });
    } finally {
      setWeeklySaving(false);
    }
  };

  const handleTransitionSave = async (transitionData) => {
    setTransitionSaving(true);
    
    try {
      await phaseTransitionService.saveReflection(user.id, transitionData);
      setShowTransitionModal(false);
      setPendingTransition(null);
      setMessage({ type: 'success', text: `Welcome to Phase ${transitionData.phaseStarting}!` });
      
      // Reload the content for the new phase
      setTimeout(() => {
        loadDailyContent();
      }, 1000);
    } catch (error) {
      console.error('Failed to save phase transition:', error);
      setMessage({ type: 'error', text: 'Failed to save phase transition reflection' });
    } finally {
      setTransitionSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading today's content..." />;
  }

  if (!content) {
    return (
      <>
        <Navigation />
        <main className="mx-auto max-w-4xl px-6 py-10">
          <div className="rounded-soft border border-warning-200 bg-warning-50 p-4 text-warning-700">
            Content for Day {day || user.currentDay} is not available yet.
          </div>
        </main>
      </>
    );
  }

  const phaseLabels = {
    1: { name: 'Stabilize', color: 'phase.stabilize' },
    2: { name: 'Understand', color: 'phase.understand' },
    3: { name: 'Rebuild', color: 'phase.rebuild' },
    4: { name: 'Sustain', color: 'phase.sustain' },
  };

  const phaseInfo = phaseLabels[content.phase] || { name: `Phase ${content.phase}`, color: 'neutral' };

  const getAlertClasses = (type) => {
    if (type === 'success') return 'border-success-200 bg-success-50 text-success-700';
    return 'border-error-200 bg-error-50 text-error-700';
  };

  const dayNumber = parseInt(day || user.currentDay, 10);
  const showFaithOption = user?.faithPreferences?.faithTrackEnabled;
  const isWeeklyDay = dayNumber % 7 === 0;
  const adaptiveTrack = adaptive?.track || null;
  const adaptiveReasons = adaptive?.reasons || [];
  const adaptiveFocus = adaptiveTrack?.focus || [];
  const adaptiveTips = adaptiveTrack?.tips || [];
  const formatFocusLabel = (pillar) => pillar.charAt(0).toUpperCase() + pillar.slice(1);

  return (
    <>
      {showTransitionModal && pendingTransition && (
        <PhaseTransitionModal
          pendingTransition={pendingTransition}
          onSave={handleTransitionSave}
          onClose={() => setShowTransitionModal(false)}
          isSaving={transitionSaving}
        />
      )}
      
      <Navigation />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-6">
          <Badge variant={phaseInfo.color} size="sm">
            Phase {content.phase}: {phaseInfo.name}
          </Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            Day {content.day} Focus
          </h2>
          <p className="mt-2 text-neutral-600">
            Work through the three pillars to keep your momentum moving forward.
          </p>
        </div>

        {message && (
          <div className={`mb-6 rounded-soft border p-4 ${getAlertClasses(message.type)}`}>
            {message.text}
          </div>
        )}

        {adaptiveTrack && (
          <Card className="mb-6" gradient="from-amber-100/40 to-white" hover>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <Badge variant="secondary" size="sm">
                  {adaptiveTrack.key === 'standard' ? 'Standard Track' : 'Adaptive Track'}
                </Badge>
                <h3 className="mt-3 text-xl font-display font-semibold text-neutral-800">
                  {adaptiveTrack.label}
                </h3>
                <p className="mt-2 text-neutral-600">{adaptiveTrack.summary}</p>
              </div>
            </div>

            {adaptiveReasons.length > 0 && (
              <div className="mt-4 rounded-soft border border-amber-200/70 bg-amber-50/60 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Signals</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
                  {adaptiveReasons.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {adaptiveFocus.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {adaptiveFocus.map((pillar) => (
                  <Badge key={pillar} variant="primary" size="sm">
                    Focus: {formatFocusLabel(pillar)}
                  </Badge>
                ))}
              </div>
            )}

            {adaptiveTips.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-neutral-700">Today&apos;s Tips</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-neutral-600">
                  {adaptiveTips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )}

        {isWeeklyDay ? (
          <Card className="mb-6" gradient="from-primary-500/10 to-primary-600/5" hover>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-800">
                  Weekly Reflection
                </h3>
                <p className="mt-2 text-neutral-600">
                  Take a moment to review your week and set a focus for the next one.
                </p>
              </div>
              <Button onClick={handleWeeklySubmit} disabled={weeklySaving || weeklyLoading}>
                {weeklySaving ? 'Saving...' : 'Save Reflection'}
              </Button>
            </div>

            {weeklyMessage && (
              <div className={`mt-4 rounded-soft border p-3 text-sm ${getAlertClasses(weeklyMessage.type)}`}>
                {weeklyMessage.text}
              </div>
            )}

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <Textarea
                label="What went well this week?"
                rows={4}
                value={weeklyReflection.wentWell}
                onChange={(e) => handleWeeklyChange('wentWell', e.target.value)}
                placeholder="Celebrate your wins and progress."
                fullWidth
              />
              <Textarea
                label="What was challenging?"
                rows={4}
                value={weeklyReflection.challenges}
                onChange={(e) => handleWeeklyChange('challenges', e.target.value)}
                placeholder="Name the moments that felt heavy."
                fullWidth
              />
              <Textarea
                label="What did you avoid?"
                rows={4}
                value={weeklyReflection.avoided}
                onChange={(e) => handleWeeklyChange('avoided', e.target.value)}
                placeholder="Notice anything you stepped around this week."
                fullWidth
              />
              <Textarea
                label="One thing to focus on next week"
                rows={4}
                value={weeklyReflection.focusNextWeek}
                onChange={(e) => handleWeeklyChange('focusNextWeek', e.target.value)}
                placeholder="Set an intention you can revisit daily."
                fullWidth
              />
            </div>

            <div className="mt-4 max-w-xs">
              <Input
                label="Self-rating (1-10)"
                type="number"
                min={1}
                max={10}
                value={weeklyReflection.selfRating}
                onChange={(e) => handleWeeklyChange('selfRating', e.target.value)}
                fullWidth
              />
            </div>
          </Card>
        ) : (
          <>
            {/* Mind Pillar */}
            <Card className="mb-6" gradient="from-pillar-mind/10 to-pillar-mind/5" hover>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-display font-semibold text-neutral-800">
                    🧠 Mind: {content.mind.title}
                  </h3>
                  <p className="mt-2 text-neutral-600">{content.mind.description}</p>
                  <p className="mt-3 text-sm text-neutral-500">Category: {content.mind.category}</p>
                </div>
                <Button
                  onClick={() => handleSubmit('mind')}
                  disabled={journalData.completed.mind || saving}
                >
                  {journalData.completed.mind ? '✓ Completed' : 'Mark as Complete'}
                </Button>
              </div>

              <div className="mt-6 space-y-4">
                {content.mind.exercises.map((exercise, index) => (
                  <div key={index} className="rounded-soft border border-neutral-100 bg-white/80 p-4">
                    <h4 className="font-semibold text-neutral-800">{exercise.title}</h4>
                    <p className="mt-2 text-sm text-neutral-600">{exercise.content}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Move Pillar */}
            <Card className="mb-6" gradient="from-primary-500/10 to-primary-600/5" hover>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-display font-semibold text-neutral-800">
                    🏃 Move: {content.move.title}
                  </h3>
                  <p className="mt-2 text-neutral-600">{content.move.description}</p>
                  <p className="mt-3 text-sm text-neutral-500">
                    Duration: {content.move.duration} minutes • Type: {content.move.activityType}
                  </p>
                </div>
                <Button
                  onClick={() => handleSubmit('move')}
                  disabled={journalData.completed.move || saving}
                >
                  {journalData.completed.move ? '✓ Completed' : 'Mark as Complete'}
                </Button>
              </div>

              <div className="mt-6 rounded-soft border border-neutral-100 bg-white/80 p-4">
                <h4 className="font-semibold text-neutral-800">Instructions</h4>
                <ol className="mt-3 space-y-2 text-sm text-neutral-600">
                  {content.move.instructions.map((instruction, index) => (
                    <li key={index}>
                      <span className="font-semibold text-neutral-700">{index + 1}.</span> {instruction}
                    </li>
                  ))}
                </ol>
              </div>
            </Card>

            {/* Journal Pillar */}
            <Card className="mb-6" gradient="from-secondary-500/10 to-secondary-600/5" hover>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-display font-semibold text-neutral-800">
                    📓 Journal: {content.journal.title}
                  </h3>
                  <p className="mt-2 text-neutral-600">Reflect on your experience today.</p>
                </div>
                <Button
                  onClick={() => handleSubmit('journal')}
                  disabled={saving || (journalLocked && !journalAddition.trim())}
                  variant="secondary"
                >
                  {saving
                    ? 'Saving...'
                    : journalLocked
                      ? (journalAddition.trim() ? 'Save Addition' : '✓ Saved')
                      : 'Save Journal Entry'}
                </Button>
              </div>

              {journalLocked && (
                <div className="mt-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
                  Your journal entry for today has been saved. You can add more thoughts below — additions will be appended. Previous text cannot be edited.
                </div>
              )}

              <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Input
                  label="Mood (1-10)"
                  type="number"
                  value={journalData.moodScale}
                  onChange={(e) => handleJournalChange('moodScale', e.target.value)}
                  min={1}
                  max={10}
                  fullWidth
                  disabled={journalLocked}
                />
                <Input
                  label="Challenge Intensity (1-10)"
                  type="number"
                  value={journalData.cravingScale}
                  onChange={(e) => handleJournalChange('cravingScale', e.target.value)}
                  min={1}
                  max={10}
                  fullWidth
                  disabled={journalLocked}
                />
                <Input
                  label="Sleep Hours"
                  type="number"
                  value={journalData.sleepHours}
                  onChange={(e) => handleJournalChange('sleepHours', e.target.value)}
                  min={0}
                  max={24}
                  step={0.5}
                  fullWidth
                  disabled={journalLocked}
                />
                <Input
                  label="Tags (comma separated)"
                  type="text"
                  value={journalData.tags}
                  onChange={(e) => handleJournalChange('tags', e.target.value)}
                  placeholder="stress, family, gratitude"
                  fullWidth
                  disabled={journalLocked}
                />
              </div>

              <div className="mt-8 space-y-6">
                {content.journal.prompts.map((prompt, index) => (
                  <div key={index} className="rounded-soft border border-neutral-100 bg-white/80 p-4">
                    <p className="font-medium text-neutral-800">{prompt.question}</p>
                    {prompt.guidingText && (
                      <p className="mt-1 text-xs text-neutral-500">{prompt.guidingText}</p>
                    )}
                    <Textarea
                      className="mt-3"
                      fullWidth
                      rows={4}
                      value={journalData.responses[index] || ''}
                      onChange={(e) => handleResponseChange(index, e.target.value)}
                      placeholder="Write your thoughts here..."
                      disabled={journalLocked}
                    />
                  </div>
                ))}
              </div>

              {/* Saved free-write shown read-only when locked */}
              {journalLocked && savedJournalContent && (
                <div className="mt-6 rounded-soft border border-neutral-200 bg-white/80 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-2">Saved entry</p>
                  <div className="text-sm text-neutral-700 whitespace-pre-wrap">
                    {savedJournalContent}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <Textarea
                  label={journalLocked ? 'Add more to today\'s entry' : 'Free-write (optional)'}
                  rows={journalLocked ? 4 : 5}
                  value={journalLocked ? journalAddition : journalData.freeWrite}
                  onChange={(e) => journalLocked
                    ? setJournalAddition(e.target.value)
                    : handleJournalChange('freeWrite', e.target.value)
                  }
                  placeholder={journalLocked
                    ? 'Add more thoughts, reflections, or notes...'
                    : 'Write anything else you want to capture today.'
                  }
                  fullWidth
                />
              </div>

              {!journalLocked && (
                <p className="mt-3 text-xs text-neutral-500">
                  Once saved, your journal entry is permanent for this day. You'll be able to add more thoughts, but previous text cannot be edited.
                </p>
              )}
            </Card>
          </>
        )}

        {showFaithOption && (
          <Card className="mb-6" gradient="from-blue-600/10 to-blue-500/5" hover>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-800">
                  ✨ Faith: Biblical Foundations
                </h3>
                <p className="mt-2 text-neutral-600">
                  Optional daily scripture and reflection to support your journey.
                </p>
                {faithLoading && (
                  <p className="mt-3 text-sm text-neutral-500">Loading faith content...</p>
                )}
                {faithError && (
                  <p className="mt-3 text-sm text-error-600">{faithError}</p>
                )}
              </div>
              <Button onClick={() => navigate(`/faith/${dayNumber || 1}`)}>
                Open Faith Pillar
              </Button>
            </div>

            {faithContent && !faithLoading && (
              <div className="mt-6 rounded-soft border border-neutral-100 bg-white/80 p-4">
                <p className="text-xs font-semibold text-blue-700">{faithContent.scriptureReference}</p>
                <h4 className="mt-2 font-semibold text-neutral-800">{faithContent.title}</h4>
                <p className="mt-2 text-sm text-neutral-600">{faithContent.themeTag}</p>
                <p className="mt-3 text-sm text-neutral-700">
                  {faithContent.reflectionPrompt}
                </p>
                <Button
                  className="mt-4"
                  variant="secondary"
                  onClick={() => navigate(`/faith/${dayNumber || 1}`)}
                >
                  Continue to Full Reflection
                </Button>
              </div>
            )}
          </Card>
        )}

        <OnThisDay userId={user.id} currentDay={dayNumber} />

        {!showFaithOption && (
          <Card className="mb-6" gradient="from-blue-50/60 to-white" hover>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-display font-semibold text-neutral-800">
                  ✨ Faith Pillar (Optional 4th Practice)
                </h3>
                <p className="mt-2 text-sm text-neutral-600">
                  Add biblical foundations and values reflection to your daily routine.
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => navigate('/settings')}
              >
                Enable in Settings
              </Button>
            </div>
          </Card>
        )}
      </main>

      {/* Floating SOS Button for crisis support */}
      <SOSButton variant="floating" />
    </>
  );
};

export default DailyContent;
